<script
  setup
  lang="ts"
  generic="T extends { id: string | number; [key: string]: any }"
>
import AlertComponent from "@/components/AlertComponent.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import { ref } from "vue";
import { Collapse } from "vue-collapsed";
import HasPermission from "./HasPermission.vue";

interface Props {
  entries: T[];
  headers: { [key: string]: string };
  title: string;
  createToggleTitle?: string;
  onDeleteTitle?: string;
  onDeleteMessage?: string;
  selectedEntry?: T;
  customGridClass?: string;
  noEntriesMessage?: string;
  showSaveButton?: boolean;
  permissionToCreate?: string;
  permissionToEdit?: string;
  permissionToDelete?: string;
  entityType?: string;
  setSelected?: (entry: T | undefined) => any;
  onCreate?: () => any;
  onDelete?: (id: string | number, ...params: any[]) => any;
  onUpdate?: (id: string | number, ...params: any[]) => any;
  onSave?: () => any;
  isDisabled?: boolean;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  noEntriesMessage: "No hay entradas para este apartado.",
  showSaveButton: true,
  onCreate: () => null,
  onSave: () => null,
  permissionToCreate: "",
  permissionToEdit: "",
  permissionToDelete: "",
});
const creatingEntry = ref(false);
const confirmingDelete = ref(false);
const onConfirmDelete = ref<() => any>();
const editingEntry = ref(false);

const withActions = props.onDelete || props.onUpdate;

const gridClass = ref(
  props.customGridClass ??
    `grid gap-x-2 grid-cols-${Object.keys(props.headers).length + (withActions ? 2 : 0)}`,
);

const setOnEditing = (entry: T) => {
  if (props.selectedEntry?.id !== entry.id) {
    props.setSelected?.(entry);
  } else {
    props.setSelected?.(undefined);
    editingEntry.value = !editingEntry.value;
  }
};

const handleCreate = () => {
  try {
    props.onCreate();
  } catch (err) {
    console.log(err);
  } finally {
    creatingEntry.value = false;
  }
};

const handleDelete = (id: string | number) => {
  try {
    if (props.onDelete) {
      props.onDelete(id);
    }
  } catch (err) {
    console.log(err);
  } finally {
    confirmingDelete.value = false;
  }
};

const handleSave = () => {
  try {
    props.onSave();
  } catch (err) {
    console.log(err);
  } finally {
    editingEntry.value = false;
  }
};
</script>

<template>
  <RoundedBox class="flex-col bg-white">
    <div class="flex justify-between mb-[1rem]">
      <h2 class="text-xl font-semibold">{{ title }}</h2>

      <HasPermission
        :name-permission="permissionToCreate"
        :entity-type="entityType || ''"
      >
        <StyledButton
          v-if="$slots['form']"
          class="bg-[#253C70] text-white ml-auto flex items-center gap-[0.75rem] !px-2"
          :on-click="() => (creatingEntry = !creatingEntry)"
        >
          <span class="material-symbols-outlined"> add_circle </span>

          <span class="text-base hidden md:block">{{ createToggleTitle }}</span>
        </StyledButton>
      </HasPermission>
    </div>

    <Collapse v-if="$slots['form']" :when="creatingEntry" class="v-collapse">
      <form @submit.prevent="handleCreate" class="flex items-end gap-[2.25rem]">
        <slot name="form" />
      </form>
    </Collapse>

    <div
      v-if="headers && entries.length"
      class="grid gap-3 overflow-x-auto overflow-y-hidden mt-[1.563rem]"
    >
      <div v-if="headers" :class="[gridClass]">
        <h3
          v-for="key in Object.keys(headers)"
          class="header max-w-[2rem]"
          :key="key"
        >
          {{ headers[key as keyof typeof headers] }}
        </h3>
      </div>

      <hr />

      <div
        :class="['items-start', gridClass]"
        v-for="entry in entries"
        :key="entry.id"
      >
        <template v-if="$slots['entry']">
          <slot
            v-for="headerKey in Object.keys(headers)"
            name="entry"
            :entry="entry"
            :entry-field="entry[headerKey]"
            :key="headerKey"
            :handleSave="handleSave"
          />
        </template>

        <template v-else>
          <template v-if="headers">
            <span v-for="headerKey in Object.keys(headers)" :key="headerKey">{{
              entry[headerKey]
            }}</span>
          </template>
        </template>

        <div
          v-if="withActions"
          class="flex col-span-2 items-center justify-end gap-2"
        >
          <HasPermission
            :name-permission="permissionToEdit"
            :entity-type="entityType || ''"
          >
            <button
              v-if="onUpdate"
              class="material-symbols-outlined text-[#2A5C8A]"
              @click.prevent="() => setOnEditing(entry)"
            >
              edit
            </button>
          </HasPermission>

          <HasPermission
            :name-permission="permissionToDelete"
            :entity-type="entityType || ''"
          >
            <button
              v-if="onDelete"
              class="material-symbols-outlined text-[#DC2626]"
              @click.prevent="
                () => {
                  onConfirmDelete = () => handleDelete(entry.id);
                  confirmingDelete = true;
                }
              "
            >
              delete
            </button>
          </HasPermission>
        </div>
      </div>
    </div>

    <div v-else class="mt-4">
      <p>{{ noEntriesMessage }}</p>
    </div>

    <AlertComponent
      v-model="confirmingDelete"
      :type="'warning'"
      :dismiss="true"
      :on-confirm="onConfirmDelete"
    >
      <template #title>
        {{ onDeleteTitle || "¿Estás seguro de eliminar este elemento?" }}
      </template>

      <template #body>
        {{ onDeleteMessage || "Esta acción no se puede deshacer." }}
      </template>

      <template #closeButton> Cancelar </template>

      <template #button> Eliminar </template>
    </AlertComponent>

    <HasPermission
      :name-permission="permissionToEdit"
      :entity-type="entityType || ''"
    >
      <StyledButton
        v-if="showSaveButton && entries.length"
        label="Guardar"
        class="bg-[#253C70] text-white ml-auto mt-[1.563rem]"
        @click="handleSave"
        :disabled="isDisabled"
        :loading="isLoading"
      />
    </HasPermission>
  </RoundedBox>
</template>

<style scoped>
.grid-set {
  /* @apply grid-cols-[repeat(4,_minmax(20px,_auto))];
   */
  grid-template-columns: minmax(20px, auto) 1fr 1fr;
}
</style>
